import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import AssaysViewBatchModalComponent from '@/components/assays-view/components/batch/batch-modal/assays-view-batch-modal.vue'
import { AssaysViewSampleNodeStructure } from '@/components/assays-view/components/modal/assays-view-modal.model'
import {
    initialAssaysViewBatchModalProtocolsState,
    AssaysViewBatchModalProtocolsState,
    AssaysViewBatchModalProtocolsCustomConfig
} from '@/components/assays-view/components/batch/batch-modal-protocols/assays-view-batch-modal-protocols.model'
import { CommonPriorityResponse } from '@/api/services/common/priority/common-priority.dto'
import { ProtocolIndexResponse } from '@/api/services/common/protocol/common-protocol.dto'
import { commonProtocolService } from '@/api/services/common/protocol/common-protocol.service'
import {
    AssaysViewApplyProtocolsRequest,
    AssaysViewPortionResponse
} from '@/components/assays-view/services/assays-view-portion/assays-view-portion.dto'
import { assaysViewPortionService } from '@/components/assays-view/services/assays-view-portion/assays-view-portion.service'
import { cloneDeep, intersection, isEqual, sortBy, uniq } from 'lodash'
import { BatchItemWrapper } from '@/components/assays-view/components/batch/batch-modal/assays-view-batch-modal.model'
import { StrictNullable } from '@/shared/types/app.types'
import { store } from '@/store/store'
import { UiComponentUtils } from '@/shared/util/ui-component-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'

@Component({
    components: {
        AssaysViewBatchModalComponent
    }
})
export default class AssaysViewBatchModalProtocols extends Vue {
    @Prop({ type: Number, required: true }) readonly casId!: number
    @Prop({ type: Object }) readonly casePriority!: CommonPriorityResponse
    @Prop({ type: Array, default: () => [] }) readonly sampleNodes!: AssaysViewSampleNodeStructure[]

    @Ref() readonly batchModal!: any

    state: AssaysViewBatchModalProtocolsState

    @Watch('state.selectedPortions')
    async onSelectedPortionsChange(): Promise<void> {
        if (this.selectedPortionsLabIdsChanged || this.state.showOnlyCompatibles) {
            await this._loadProtocolsIfNecessary()
        }
    }

    constructor() {
        super()
        this.state = initialAssaysViewBatchModalProtocolsState()
    }

    private static get _labId(): number {
        return store.state.labId
    }

    private get selectedPortionsLabIdsChanged() {
        const labIds = uniq(this.state.selectedPortions.map(portion => portion.currentLaboratory.labId))
            .sort((a, b) => a - b)
        return !isEqual(labIds, this.state.labIds)
    }

    get disabledOnlyCompatibles(): boolean {
        return !this.state.selectedPortions.length
    }

    /// OPERATIONS ///

    private async _loadProtocols(laboratoryId: number, sampleIds: number[]): Promise<ProtocolIndexResponse[]> {
        let response: StrictNullable<ProtocolIndexResponse[]>
        if (this.state.showOnlyCompatibles) {
            response =
                await PromiseUtils.toCallUnique(
                    commonProtocolService.getProtocolsBySampleIds(
                        laboratoryId,
                        this.casId,
                        sampleIds))
        } else {
            response =
                await PromiseUtils.toCallUnique(
                    commonProtocolService.getProtocols(laboratoryId))
        }
        return response ?? []
    }
    private async _loadProtocolsIfNecessary(): Promise<void> {
        this.state.loading = true
        try {
            if (this.state.selectedPortions.length) {
                const labIds = uniq(this.state.selectedPortions.map(portion => portion.currentLaboratory.labId))
                const sampleIds = this.state.selectedPortions.map(portion => portion.sampleId)
                let response: ProtocolIndexResponse[] = []
                for (let i = 0; i < labIds.length; i++) {
                    const protocols = await this._loadProtocols(labIds[i], sampleIds)
                    if (i === 0) {
                        response = protocols
                    } else {
                        // TODO: backend should do the intersection (reduce logic in front)
                        response = intersection(response, protocols)
                    }
                }
                this.state.labIds = labIds
                this.state.protocols = sortBy(response, 'prtName')
            }
        } finally {
            this.state.loading = false
        }
    }

    async onChangeShowOnlyCompatibles(): Promise<void> {
        const config: AssaysViewBatchModalProtocolsCustomConfig = {
            showOnlyCompatibles: this.state.showOnlyCompatibles
        }
        await UiComponentUtils.saveCustomConfig<AssaysViewBatchModalProtocolsCustomConfig>(
            this.state.uiComponentCode,
            { config },
            AssaysViewBatchModalProtocols._labId)

        await this._loadProtocolsIfNecessary()
    }

    batchItemIdResolver(protocolIndex: ProtocolIndexResponse): string {
        return `${protocolIndex.prtId}`
    }
    batchItemNameResolver(protocolIndex: ProtocolIndexResponse): string {
        return protocolIndex.prtName
    }

    async save(selectedBatchItemWrappers: BatchItemWrapper<ProtocolIndexResponse>[]): Promise<void> {
        const request: AssaysViewApplyProtocolsRequest = {
            protocols:
                selectedBatchItemWrappers.map(batchItemWrapper => ({
                    protocolId: batchItemWrapper.batchItem.prtId,
                    priorityId: batchItemWrapper.priority.id
                })),
            hierarchicalPortionTreeRequests:
                this.state.selectedPortions.map(portion => ({
                    sampleId: portion.sampleId,
                    portionId: portion.portionId
                }))
        }
        const response =
            await PromiseUtils.toCallUnique(
                assaysViewPortionService.applyPortions(
                    AssaysViewBatchModalProtocols._labId,
                    this.casId,
                    request))
        if (response !== null) {
            ModalUtils.showSuccessModal(this.$tc('component.assaysViewModal.protocolsCreatedSuccess'))
            this.$emit('create')
            this.batchModal.hide()
        }
    }

    /// MODAL ///

    async show(selectedPortions: AssaysViewPortionResponse[]): Promise<void> {
        this.state = initialAssaysViewBatchModalProtocolsState()
        const customConfig =
            await UiComponentUtils.getCustomConfig<AssaysViewBatchModalProtocolsCustomConfig>(
                this.state.uiComponentCode,
                AssaysViewBatchModalProtocols._labId)
        if (customConfig) {
            this.state.showOnlyCompatibles = customConfig.config.showOnlyCompatibles
        }
        this.state.selectedPortions = cloneDeep(selectedPortions)
        await this.batchModal.show()
    }
    hide(): void {
        this.batchModal.hide()
    }
}
